"use client";

import dynamic from "next/dynamic";
import { useCallback, useEffect, useState } from "react";
import type { VargaConfig } from "@/lib/varga-charts-data";

const DEFAULT_DEFER_MS = 8000;
const ACTIVATE_EVENTS = ["pointerdown", "keydown", "touchstart", "focusin"] as const;

const VargaChartClient = dynamic(() => import("./VargaChartClient"), {
  ssr: false,
  loading: () => <VargaChartReserve />,
});

export default function DeferredVargaChartClient({
  config,
  deferMs = DEFAULT_DEFER_MS,
}: {
  config: VargaConfig;
  deferMs?: number;
}) {
  const [ready, setReady] = useState(false);
  const activate = useCallback(() => setReady(true), []);

  useEffect(() => {
    if (ready) return;

    const timer = window.setTimeout(activate, deferMs);
    const options: AddEventListenerOptions = { once: true, passive: true };
    for (const eventName of ACTIVATE_EVENTS) {
      window.addEventListener(eventName, activate, options);
    }

    return () => {
      window.clearTimeout(timer);
      for (const eventName of ACTIVATE_EVENTS) {
        window.removeEventListener(eventName, activate);
      }
    };
  }, [activate, deferMs, ready]);

  if (ready) return <VargaChartClient config={config} />;

  return <VargaChartShell config={config} onActivate={activate} />;
}

function VargaChartShell({ config, onActivate }: { config: VargaConfig; onActivate: () => void }) {
  return (
    <div className="mb-10 mt-6" onFocus={onActivate} onPointerDown={onActivate}>
      <form
        aria-busy="true"
        className="card-sacred min-h-[350px] p-5 sm:p-6"
        onSubmit={(event) => {
          event.preventDefault();
          onActivate();
        }}
      >
        <p
          className="mb-3 text-[13px] font-semibold uppercase tracking-wide"
          style={{ color: "var(--ish-accent)" }}
        >
          Free {config.nameEn} Chart Calculator
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ShellField label="Name (optional)" />
          <ShellField label="Date of Birth *" />
          <ShellField label="Time of Birth" />
          <ShellField label="Place of Birth *" />
        </div>
        <button
          type="submit"
          className="mt-4 w-full rounded-md px-6 py-2.5 text-[14px] font-semibold transition-opacity sm:w-auto"
          style={{
            backgroundColor: "var(--ish-accent)",
            color: "#fff",
          }}
        >
          Generate {config.nameEn} Chart
        </button>
      </form>
    </div>
  );
}

function VargaChartReserve() {
  return (
    <div className="mb-10 mt-6" aria-hidden>
      <div className="card-sacred min-h-[350px] p-5 sm:p-6" />
    </div>
  );
}

function ShellField({ label }: { label: string }) {
  return (
    <label className="block">
      <span
        className="mb-1 block text-[12px] font-medium"
        style={{ color: "var(--ish-text-muted)" }}
      >
        {label}
      </span>
      <span
        className="block h-10 rounded-md"
        style={{
          backgroundColor: "var(--ish-surface, #fffdf9)",
          border: "1px solid var(--ish-border)",
        }}
      />
    </label>
  );
}
